import React, { useCallback, useEffect, useRef } from 'react';
import generatePicker from 'antd/lib/date-picker/generatePicker';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
// eslint-disable-next-line import/no-extraneous-dependencies
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import { useTranslation } from 'react-i18next';
import InputLabel from 'commons/src/components/input/InputLabel';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { dateFormats } from 'commons/src/constants';

const MOBILE_BREAKPOINT = 890;

export type IAQDatePickerProps = {
    id: string;
    onChange: (date: Moment) => void;
    dateFormat: keyof typeof dateFormats;
    placeholder?: string;
    label?: string;
    mandatory?: boolean;
    initialVisibleMonth?: Moment;
    defaultValue?: Moment;
    disabled?: boolean;
    disabledDate?: (moment: Moment) => boolean;
    showToday?: boolean;
    displayDateValidation?: boolean;
    selectedDate?: Moment | null;
    selectedDateHint?: string;
    infoText?: string;
    standardWidth?: boolean;
    showHint?: boolean;
    testId?: string;
    month?: boolean;
};

const DatePickerWithMoment = generatePicker<Moment>(momentGenerateConfig);

const IAQDatePicker: React.FC<IAQDatePickerProps> = props => {
    const {
        label,
        id,
        defaultValue,
        onChange,
        showToday = false,
        dateFormat,
        disabled = false,
        disabledDate,
        mandatory = false,
        initialVisibleMonth,
        displayDateValidation,
        selectedDate,
        selectedDateHint,
        infoText,
        placeholder,
        standardWidth,
        showHint = true,
        testId,
        month,
    } = props;
    const { t: txt } = useTranslation();
    const scrollLockedRef = useRef(false);
    const scrollPositionRef = useRef(0);

    const lockBodyScroll = useCallback(() => {
        if (window.innerWidth <= MOBILE_BREAKPOINT) {
            scrollPositionRef.current = window.scrollY;
            document.body.style.setProperty('--scroll-position', `-${scrollPositionRef.current}px`);
            document.body.classList.add('iaq-datepicker-scroll-lock');
            scrollLockedRef.current = true;
        }
    }, []);

    const unlockBodyScroll = useCallback(() => {
        if (scrollLockedRef.current) {
            document.body.classList.remove('iaq-datepicker-scroll-lock');
            document.body.style.removeProperty('--scroll-position');
            window.scrollTo(0, scrollPositionRef.current);
            scrollLockedRef.current = false;
        }
    }, []);

    useEffect(() => {
        return () => {
            unlockBodyScroll();
        };
    }, [unlockBodyScroll]);

    const handleOpenChange = (open: boolean): void => {
        if (open) {
            lockBodyScroll();
        } else {
            unlockBodyScroll();
        }
    };

    const disabledRange = disabledDate || ((date: Moment): boolean => date > moment());

    const onDateSelect = (value: Moment | null): void => {
        if (value !== null) {
            onChange(value);
        }
    };

    const panelRender = (panelNode: React.ReactNode): React.ReactNode => (
        <div className="iaq-date-picker-panel-with-header">
            <div className="iaq-date-picker-panel-header">{txt(placeholder || 'Date')}</div>
            {panelNode}
        </div>
    );

    return (
        <div
            className={classNames('input-container', {
                'input-container--invalid': displayDateValidation,
                'form__field--standard-width': standardWidth,
            })}
        >
            {label && <InputLabel htmlFor={id} label={label} mandatory={mandatory} infoText={infoText} />}
            <DatePickerWithMoment
                id={id}
                onChange={onDateSelect}
                placeholder={txt(placeholder || 'Date')}
                format={!month ? dateFormats[dateFormat].calendarFormat : undefined}
                suffixIcon={<MaterialIcon name="date_range" />}
                allowClear={false}
                defaultPickerValue={initialVisibleMonth}
                showToday={showToday}
                disabled={disabled}
                disabledDate={disabledRange}
                value={selectedDate}
                defaultValue={defaultValue}
                data-testid={testId}
                picker={month ? 'month' : undefined}
                panelRender={panelRender}
                popupClassName="iaq-report-datepicker"
                inputReadOnly
                onOpenChange={handleOpenChange}
            />
            {displayDateValidation && showHint && (
                <div className="input-container__error">
                    {selectedDate === null ? txt('InvalidDate') : txt(selectedDateHint || '')}
                </div>
            )}
        </div>
    );
};

export default IAQDatePicker;
